import { paginateDialogue, type DialogueGlyph, type DialoguePage, type DialogueSpan } from "../../../shared/src/social/dialogue";

/** Fixed two-line viewport. Invisible glyphs retain their final geometry; revealing
 * a word can never move it to a different line. No generated text becomes HTML. */
export class DialogueTextView {
  private text = "";
  private spans: readonly DialogueSpan[] = [];
  private pages: DialoguePage[] = [];
  private glyphElements: { end: number; el: HTMLElement }[] = [];
  private page = -1;
  private visible = 0;
  private readonly context = document.createElement("canvas").getContext("2d")!;
  private readonly observer: ResizeObserver;
  private width = 0;
  private font = "";
  private fontSize = 20;

  constructor(readonly el: HTMLElement, private readonly onLayout?: (ends: number[]) => void) {
    el.style.cssText += ";height:3.6em;min-height:3.6em;max-height:3.6em;overflow:hidden;white-space:normal";
    el.setAttribute("aria-hidden", "true");
    this.observer = new ResizeObserver(() => {
      const width = el.clientWidth;
      const style = getComputedStyle(el), font = `${style.fontSize} ${style.fontFamily}`;
      if (width > 0 && (width !== this.width || font !== this.font)) {
        const anchor = this.pages[this.page]?.start ?? 0;
        const ends = this.layout();
        this.page = -1;
        this.render(this.visible, Math.max(0, this.pages.findIndex(p => p.end > anchor)));
        this.onLayout?.(ends);
      }
    });
    this.observer.observe(el);
  }

  setContent(text: string, spans: readonly DialogueSpan[] = []): number[] {
    this.text = text; this.spans = spans; this.page = -1; this.visible = 0;
    const ends = this.layout(); this.render(0, 0); return ends;
  }
  private layout(): number[] {
    const style = getComputedStyle(this.el);
    this.fontSize = parseFloat(style.fontSize) || 20;
    this.font = `${style.fontSize} ${style.fontFamily}`;
    this.width = this.el.clientWidth || 300;
    this.pages = paginateDialogue(this.text, this.spans, this.width - 2, glyphs => {
      let width = 0;
      for (const g of glyphs) {
        const size = this.fontSize * (g.style.size === "large" ? 1.25 : g.style.size === "small" ? 0.85 : 1);
        this.context.font = `${g.style.italic ? "italic" : "normal"} ${g.style.bold ? "700" : "400"} ${size}px ${style.fontFamily}`;
        width += this.context.measureText(g.text).width;
      }
      return width;
    });
    return this.pages.map(p => p.end);
  }
  render(visible: number, page = this.page < 0 ? 0 : this.page): void {
    this.visible = visible;
    const selected = this.pages[page];
    if (!selected) return;
    if (this.page !== page) {
      this.page = page; this.el.replaceChildren(); this.glyphElements = [];
      for (const line of selected.lines) {
        const row = document.createElement("div");
        row.className = "dialogue-text-row";
        row.style.cssText = "height:1.8em;display:flex;align-items:center;white-space:pre;font-kerning:none;font-variant-ligatures:none";
        for (const glyph of line.glyphs) row.appendChild(this.glyph(glyph));
        this.el.appendChild(row);
      }
    }
    for (const glyph of this.glyphElements) glyph.el.style.visibility = glyph.end <= visible ? "visible" : "hidden";
  }
  private glyph(glyph: DialogueGlyph): HTMLElement {
    const el = document.createElement("span"); el.textContent = glyph.text;
    el.style.cssText = "flex-shrink:0;line-height:1.3";
    el.style.fontWeight = glyph.style.bold ? "700" : "400";
    el.style.fontStyle = glyph.style.italic ? "italic" : "normal";
    el.style.fontSize = glyph.style.size === "large" ? "1.25em" : glyph.style.size === "small" ? ".85em" : "1em";
    el.dataset.start = String(glyph.start);
    this.glyphElements.push({ end: glyph.end, el }); return el;
  }
  get pageCount(): number { return this.pages.length; }
  get pageText(): string { const p = this.pages[this.page]; return p ? this.text.slice(p.start, p.end) : ""; }
  destroy(): void { this.observer.disconnect(); }
}
